import { ChevronLeft, ChevronRight, Copy06, PhoneCall01, Truck01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    processOrderData,
    getCpStatusTitleParts,
    buildItemsPhrase,
    NbaWordingContext,
    StatusTitleResult,
    TitleParts,
    DynamicFunctionConfig,
    getDeliveringLegId,
    getLegInstructionNoteType,
    isPostDeliveryOrderStatus,
} from 'src/app/Orders/order.helpers';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import PhoneNumberDisplay from 'src/components/PhoneNumberDisplay';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { ROUTES } from 'src/constants/routes';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { currentUser } from 'src/redux/auth.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { updateCurrentLegField, updateOrderField } from 'src/redux/orders.slice';
import { prependActivity } from 'src/redux/Quoting.slice';
import { requestUpdateLocation } from 'src/services/OrderService';
import { extractMcNumber, getShortName } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { buildAwaitingStopTitleParts } from './awaitingStop.helpers';
import LegInstructionsModal from './LegInstructionsModal';
import { getCardFooterLabel, getLegNumbering, getLiveLegsSorted, getPhysicallyActiveLeg, isLegPhysicallyDone, isLegsDisplayed, normalizeLegStatus } from './nba.helpers';
import ProgressList from './ProgressList';
import UploadCustomsDocModal from './UploadCustomsDocModal';

// ── Mirrors ActionTitle.tsx / TitleDescriptionSection from wal-frontend-v2 ────

const processDynamicContent = (content: string, functions?: DynamicFunctionConfig[]): string => {
    if (!functions || functions.length === 0) return content;

    let processed = content;

    functions.forEach((config) => {
        try {
            const result = config.function(...config.args);
            const placeholderRegex = new RegExp(config.placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
            processed = processed.replace(placeholderRegex, result || '');
        } catch (error) {
            console.error(`Error processing placeholder ${config.placeholder}:`, error);
        }
    });

    return processed;
};

const hasDynamicPlaceholder = (text: string): boolean => /\{\{.*?\}\}/.test(text);

// A leg's `documents` arrives as an array from the order detail payload, but older/socket-patched
// shapes can still carry the raw JSON string — normalize once so callers never have to.
const parseLegDocuments = (rawDocuments: any): any[] => {
    if (Array.isArray(rawDocuments)) return rawDocuments;

    if (typeof rawDocuments === 'string') {
        try {
            const parsed = JSON.parse(rawDocuments);

            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    return [];
};

interface TitleDescriptionSectionProps {
    title: TitleParts;
    description: string;
    functions?: DynamicFunctionConfig[];
}

const TitleDescriptionSection: React.FC<TitleDescriptionSectionProps> = ({ title, description, functions }) => {
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const hasFunctions = Boolean(functions && functions.length > 0);
    const titleHasPlaceholder = hasDynamicPlaceholder(title.primary) || !!(title.secondary && hasDynamicPlaceholder(title.secondary));
    const descriptionHasPlaceholder = hasDynamicPlaceholder(description);
    const shouldProcessTitle = titleHasPlaceholder && hasFunctions;
    const shouldProcessDescription = descriptionHasPlaceholder && hasFunctions;
    const hasDynamicContent = shouldProcessTitle || shouldProcessDescription;

    useEffect(() => {
        if (!hasDynamicContent) return;

        const interval = setInterval(() => {
            if (!document.hidden) {
                setRefreshKey((prev) => prev + 1);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [hasDynamicContent, functions]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const processedPrimary = useMemo(() => (shouldProcessTitle ? processDynamicContent(title.primary, functions) : title.primary), [title.primary, shouldProcessTitle, functions, refreshKey]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const processedSecondary = useMemo(
        () => (title.secondary ? (shouldProcessTitle ? processDynamicContent(title.secondary, functions) : title.secondary) : undefined),
        [title.secondary, shouldProcessTitle, functions, refreshKey]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const processedDescription = useMemo(
        () => (shouldProcessDescription ? processDynamicContent(description, functions) : description),
        [description, shouldProcessDescription, functions, refreshKey]
    );

    // Separate the bold status word from its gray continuation with a period — but don't
    // double up if the primary already ends in punctuation (e.g. "Order Delivered!").
    const primaryEndsWithPunctuation = /[.!?]$/.test(processedPrimary.trim());
    const primarySuffix = processedSecondary && !primaryEndsWithPunctuation ? '.' : '';

    return (
        <div className="flex-1 space-y-0.5 min-w-0">
            <h6 className="text-grayLight900 text-sm lg:text-base font-semibold">
                {processedPrimary}
                {primarySuffix} {processedSecondary && <span className={`text-gray500 ${title.secondaryTitleClassName || ''}`}>{processedSecondary}</span>}
            </h6>
            {processedDescription && (
                <p ref={descriptionRef} className="text-grayLight600 text-[12px] leading-[18px] font-normal">
                    {processedDescription}
                </p>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

interface IOrderStatusProps {
    isLoading: boolean;
    order: any;
    currentLegData: any;
    currentLocation: any;
    isReadOnly?: boolean;
}

// eslint-disable-next-line max-lines-per-function
const OrderStatus = ({ isLoading, order, currentLegData, currentLocation, isReadOnly = false }: IOrderStatusProps) => {
    const { t } = useTranslation();
    const orgBranding = useOrgBranding();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const user = useAppSelector(currentUser);
    const salesRep = useMemo(() => user?.salesRep, [user?.salesRep]);

    // ── WAL-style leg pager ──────────────────────────────────────────────────
    // The card pages through legs with the header arrows, independent of the leg tabs
    // further down the page. It opens on the leg the freight is PHYSICALLY on (a leg
    // parked at `delivered` awaiting paperwork does not hold the pointer — that was
    // WAL's wrong-stage bug), and re-seeds only when a different order loads.
    const liveLegs = useMemo(() => getLiveLegsSorted(order?.orderLegs), [order?.orderLegs]);
    // "Display Order Legs" visibility setting. Off (the default) means the backend already merged
    // internal legs and only the customer's stops remain, so the card talks stops, never legs.
    const legsDisplayed = isLegsDisplayed(order);
    const [viewingLegIndex, setViewingLegIndex] = useState<number>(0);
    const seededOrderIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (!liveLegs.length || seededOrderIdRef.current === order?.id) return;
        const activeLeg = getPhysicallyActiveLeg(liveLegs);
        const activeIndex = liveLegs.findIndex((leg: any) => leg?.id === activeLeg?.id);
        setViewingLegIndex(activeIndex >= 0 ? activeIndex : 0);
        seededOrderIdRef.current = order?.id;
    }, [liveLegs, order?.id]);

    const viewingLeg = liveLegs[viewingLegIndex] ?? currentLegData ?? null;
    const isFirstLeg = liveLegs.length <= 1 || viewingLeg?.id === liveLegs[0]?.id;
    const isFinalLeg = liveLegs.length <= 1 || viewingLeg?.id === liveLegs[liveLegs.length - 1]?.id;

    // The freight has "reached" this leg when the previous leg physically finished — or the
    // leg itself is already moving (data can jump ahead). Until then the progress bar must
    // stay fully pending: nothing runs on a leg the freight hasn't arrived at.
    const isLegReached = useMemo(() => {
        if (isFirstLeg) return true;
        const viewingStatus = normalizeLegStatus(viewingLeg?.orderStatus);
        const viewingLegStarted = !['booked', 'dispatched', 'booking-in-progress', 'booking-failed'].includes(viewingStatus) || !!viewingLeg?.pickupDeliverySchedule?.actualPickupDatetime;
        const prevLeg = liveLegs[viewingLegIndex - 1];

        return viewingLegStarted || !prevLeg || isLegPhysicallyDone(prevLeg?.orderStatus);
    }, [isFirstLeg, viewingLeg, liveLegs, viewingLegIndex]);

    // Order-level currentLocation is the newest ping across ALL legs — only let it feed
    // this card when it actually belongs to the viewed leg, otherwise leg 1 would show
    // leg 3's position. (getCpStatusTitleParts prefers viewingLeg.lastUpdatedLocation anyway.)
    const viewingLegLocation = useMemo(() => (currentLocation?.orderLegId === viewingLeg?.id ? currentLocation : null), [currentLocation, viewingLeg?.id]);

    // Patch a field on the viewed leg everywhere it lives in Redux (master copy + the
    // currentLegData mirror when they are the same leg) so optimistic updates survive paging.
    const patchViewingLegField = (field: string, value: any) => {
        if (!viewingLeg) return;

        if (order?.orderLegs) {
            dispatch(
                updateOrderField({
                    field: 'orderLegs',
                    value: order.orderLegs.map((leg: any) => (leg?.id === viewingLeg.id ? { ...leg, [field]: value } : leg)),
                })
            );
        }

        if (currentLegData?.id === viewingLeg.id) dispatch(updateCurrentLegField({ field, value }));
    };

    const [activeLegData, setActiveLegData] = useState<any>(null);

    const [isCarrierAssigned, setIsCarrierAssigned] = useState<boolean>(false);

    const [documents, setDocuments] = useState<any>([]);

    // Process the VIEWED leg (pager-driven) when it changes
    useEffect(() => {
        if (viewingLeg) {
            const processedData = processOrderData(viewingLeg);
            setActiveLegData(processedData);
            setDocuments(processedData?.documents || []);
        }
    }, [viewingLeg, order]);

    useEffect(() => {
        // CP backend returns carrierPrice as a single object — check directly, not via array index
        const cpCarrierPrice = viewingLeg?.carrierPrice;

        setIsCarrierAssigned(!!(cpCarrierPrice && (cpCarrierPrice?.carrierId || cpCarrierPrice?.carrierName)));
    }, [viewingLeg]);

    const [isShowLegInstructionsModal, setIsShowLegInstructionsModal] = useState(false);
    const [isShowLoginRequiredModal, setIsShowLoginRequiredModal] = useState(false);
    const [isShowUploadCustomsDocModal, setIsShowUploadCustomsDocModal] = useState(false);
    const [carrierImageError, setCarrierImageError] = useState(false);
    const [isRequestingUpdateLocation, setIsRequestingUpdateLocation] = useState(false);
    const [isContactPopoverOpen, setIsContactPopoverOpen] = useState(false);
    const contactPopoverRef = useRef<HTMLDivElement>(null);
    const salesRepEmailRef = useRef<HTMLParagraphElement>(null);

    // Close contact popover on outside click or scroll
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (contactPopoverRef.current && !contactPopoverRef.current.contains(e.target as Node)) {
                setIsContactPopoverOpen(false);
            }
        };

        const handleScroll = () => setIsContactPopoverOpen(false);

        if (isContactPopoverOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, true);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isContactPopoverOpen]);

    const handleCopy = (value: string) => {
        navigator.clipboard.writeText(value);
        WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
    };

    const isUpdateLocationPending = useMemo(() => order?.customerRequests?.some((req: any) => req.type === 'update_location' && req.status === 'pending') ?? false, [order?.customerRequests]);

    const handleRequestUpdateLocation = () => {
        setIsRequestingUpdateLocation(true);
        requestUpdateLocation(order?.id)
            .then((response: any) => {
                const newRequest = response?.data?.data;

                if (newRequest) {
                    const updatedRequests = [...(order?.customerRequests || []), newRequest];
                    dispatch(updateOrderField({ field: 'customerRequests', value: updatedRequests }));
                }
                WalToast.success('Location update requested');
            })
            .finally(() => setIsRequestingUpdateLocation(false));
    };

    const handleCustomsDocUploadSuccess = (newDocument: any) => {
        // Interceptor unwraps response.data, then UploadCustomsDocModal passes response?.data
        // Final shape received here: { document: { id, fileName, uploadedByUser, ... }, activity }
        const doc = newDocument?.document;

        if (!doc) return;

        patchViewingLegField('documents', [...parseLegDocuments(viewingLeg?.documents), doc]);

        const activity = newDocument?.activity;
        if (activity) dispatch(prependActivity(activity));
    };

    // Carrier data — CP backend (wal-customer-portal-be) returns:
    //   leg.carrierPrice = single object (or null), .carrier = already-parsed JS object
    //   carrier.image = filename only, carrier.imageUrl = base URL → concat for full URL
    //   carrier.mcId = MC number (added to backend query), may be stored with an "MC" prefix
    const carrierPriceData = viewingLeg?.carrierPrice || null;
    const carrierObj = carrierPriceData?.carrier || null;
    const carrierName = carrierObj?.name || null;
    const carrierImageSrc = carrierObj?.imageUrl && carrierObj?.image ? `${carrierObj.imageUrl}${carrierObj.image}` : carrierObj?.imageUrl || carrierObj?.image || null;
    // Digits only under a "MC #" label — same format as WALHQ's CarrierHeader (avoids "MC #MC000862").
    const mcId = extractMcNumber(carrierObj?.mcId);
    const orgMcNumber = extractMcNumber(orgBranding.mcNumber);
    const carrierOrderId = viewingLeg?.carrierOrderId || null;
    const isNoCarrierAssigned = !carrierPriceData || !carrierPriceData.carrierId;
    const isUnAssignedCarrier = !!(carrierPriceData && carrierPriceData.carrierId && !carrierObj);

    // Reset image error when the viewed leg changes
    useEffect(() => {
        setCarrierImageError(false);
    }, [viewingLeg]);

    // ── NBA wording context (client wording matrix, Aug 2026) ──────────────────
    // The "Stop N of M" indicator lives only in the card footer — titles stay clean.
    const handlingUnitOptions = useAppSelector((state: any) => state.basic.handlingUnitOptions);

    // Map every PHYSICAL leg id to its presented index — line items keep physical leg ids
    // even when Show-as-Multi-Leg is off and legs are merged (mergedLegIds).
    const legIndexById = useMemo(() => {
        const map = new Map<any, number>();
        liveLegs.forEach((leg: any, index: number) => {
            (leg?.mergedLegIds ?? [leg?.id]).forEach((id: any) => map.set(id, index));
        });

        return map;
    }, [liveLegs]);

    // Item phrases for the viewed leg ("4 pallets"): boarding at this leg's start, riding this
    // leg, and ending at this leg's end. Items with no leg routing board at the start and ride
    // to the end (single-leg orders always count everything).
    const nbaContext = useMemo((): NbaWordingContext => {
        const dims: any[] = order?.order_dimensions ?? [];
        const isSingleLeg = liveLegs.length <= 1;
        const pickupIndexOf = (dim: any) => legIndexById.get(dim?.pickupLegId) ?? 0;
        const dropIndexOf = (dim: any) => legIndexById.get(dim?.dropLegId) ?? liveLegs.length - 1;

        const nextLeg = liveLegs[viewingLegIndex + 1];

        return {
            itemsPickedUp: buildItemsPhrase(isSingleLeg ? dims : dims.filter((dim: any) => pickupIndexOf(dim) === viewingLegIndex), handlingUnitOptions),
            itemsOnBoard: buildItemsPhrase(isSingleLeg ? dims : dims.filter((dim: any) => pickupIndexOf(dim) <= viewingLegIndex && dropIndexOf(dim) >= viewingLegIndex), handlingUnitOptions),
            itemsDelivered: buildItemsPhrase(isSingleLeg ? dims : dims.filter((dim: any) => dropIndexOf(dim) === viewingLegIndex), handlingUnitOptions),
            // Non-final legs read as arrivals (multi-leg wording): where this leg arrives and where the freight goes next.
            isFinalStop: isSingleLeg || isFinalLeg,
            arrivalCity: viewingLeg?.consignee?.consigneeAddress?.city || viewingLeg?.consignee?.companyName || null,
            nextStopCity: nextLeg?.consignee?.consigneeAddress?.city || nextLeg?.consignee?.companyName || null,
        };
    }, [order?.order_dimensions, liveLegs, viewingLegIndex, legIndexById, handlingUnitOptions, isFinalLeg, viewingLeg]);

    // Footer numbering shared with WALHQ: a Leg is one carrier engagement, a Stop a call within it.
    const legNumbering = useMemo(() => getLegNumbering(viewingLeg, liveLegs), [viewingLeg, liveLegs]);

    // Status title parts for Section 4.2 — derived dynamically from status + schedule/location data.
    // Location precedence inside: viewingLeg.lastUpdatedLocation → viewingLegLocation (leg-scoped).
    const statusTitleParts = useMemo(
        () => getCpStatusTitleParts(activeLegData, viewingLeg, isCarrierAssigned, documents, viewingLegLocation, nbaContext),
        [activeLegData, viewingLeg, isCarrierAssigned, documents, viewingLegLocation, nbaContext]
    );

    // Later stops before the shipment gets there: while the viewed leg has not physically started,
    // the truthful state is "on its way here" or "arrived here, leaving next", not the leg's own
    // booked card. The copy is built in buildAwaitingStopTitleParts from the customer's side
    // (no "leg", no leg ids, no carrier talk unless legs are displayed).
    const runAwaitingTitleParts = useMemo((): StatusTitleResult | null => {
        if (isFirstLeg || !viewingLeg) return null;

        const status = normalizeLegStatus(viewingLeg?.orderStatus);
        const notStarted = ['booked', 'dispatched', 'booking-in-progress', 'booking-failed'].includes(status);
        if (!notStarted || viewingLeg?.pickupDeliverySchedule?.actualPickupDatetime) return null;

        const prevLeg = liveLegs[viewingLegIndex - 1];
        if (!prevLeg) return null;

        // Every page is the stop the leg departs from (where the shipment arrives next); the leg's
        // destination is where it goes afterwards, on the last leg the final delivery.
        const city = viewingLeg?.shipper?.shipperAddress?.city || viewingLeg?.shipper?.companyName || '';
        const nextCity = viewingLeg?.consignee?.consigneeAddress?.city || viewingLeg?.consignee?.companyName || '';

        // Pickup/drop at this stop from the customer's ACTUAL line-item routing: items ending on
        // the PREVIOUS leg are delivered here, items starting on THIS leg are picked up here.
        // When legs are hidden the backend merges legs but line items keep their PHYSICAL leg
        // ids, so match every physical leg the presented leg covers (mergedLegIds).
        const dims: any[] = order?.order_dimensions ?? [];
        const viewingLegIds: any[] = viewingLeg?.mergedLegIds ?? [viewingLeg?.id];
        const prevLegIds: any[] = prevLeg?.mergedLegIds ?? [prevLeg?.id];
        const pickupDims = dims.filter((dim: any) => viewingLegIds.includes(dim?.pickupLegId));
        const dropDims = dims.filter((dim: any) => prevLegIds.includes(dim?.dropLegId));

        return buildAwaitingStopTitleParts({
            legsDisplayed,
            isFinalLeg,
            prevStatus: normalizeLegStatus(prevLeg?.orderStatus),
            prevPhysicallyDone: isLegPhysicallyDone(prevLeg?.orderStatus),
            city,
            nextCity,
            hasPickupHere: pickupDims.length > 0,
            hasDropHere: dropDims.length > 0,
            isSameCarrierRun: !!viewingLeg?.carrierPrice?.isCoveredCarrier || viewingLeg?.carrierSourceLegId != null,
            pickupItems: buildItemsPhrase(pickupDims, handlingUnitOptions),
            dropItems: buildItemsPhrase(dropDims, handlingUnitOptions),
            finalItems: nbaContext.itemsDelivered,
        });
    }, [isFirstLeg, isFinalLeg, viewingLeg, liveLegs, viewingLegIndex, order?.order_dimensions, nbaContext.itemsDelivered, handlingUnitOptions, legsDisplayed]);

    const effectiveTitleParts = runAwaitingTitleParts ?? statusTitleParts;

    const handleActionClick = (isOpen: boolean) => {
        setIsShowLoginRequiredModal(isOpen);
    };

    // The physical leg the customs documents must be filed on. With "Display Order Legs" off the
    // backend merges internal legs into one presented span (mergedLegIds) and names the hidden leg
    // that actually crosses the border as `customsLegId` — WALHQ reads customs papers off THAT leg,
    // so uploading to the span's identity leg would file them on the wrong leg. A presented leg
    // without that field is a real physical leg: it is the crossing leg itself when its shipper and
    // consignee sit in different countries (codes lowercased so "US" vs "us" is not a crossing).
    // null = nothing crosses a border here, so the upload button is hidden.
    const customsLegId = useMemo(() => {
        if (viewingLeg?.customsLegId !== undefined) return viewingLeg.customsLegId ?? null;

        const shipperCountry = viewingLeg?.shipper?.shipperAddress?.countryCode?.toLowerCase();
        const consigneeCountry = viewingLeg?.consignee?.consigneeAddress?.countryCode?.toLowerCase();
        const crossesBorder = !!shipperCountry && !!consigneeCountry && shipperCountry !== consigneeCountry;

        return crossesBorder ? viewingLeg?.id ?? null : null;
    }, [viewingLeg]);
    const isCrossBorder = customsLegId != null;

    // Customs paperwork is filed once: with a customs document already on this leg the button has
    // nothing left to ask for, so it goes away rather than inviting a duplicate. Same rule the Track
    // page applies (its backend `hasCustomsDocument` flag is this EXISTS check in SQL).
    //
    // Read off the leg's documents in redux rather than a one-way flag, so removing the document
    // from the Documents section brings the button straight back — removeLegDocument patches this
    // same array. Voided documents never reach the client (orderDetail.service filters them).
    const hasCustomsDocument = useMemo(() => parseLegDocuments(viewingLeg?.documents).some((doc: any) => doc?.documentType === 'CUSTOMS_DOCUMENT'), [viewingLeg?.documents]);

    // ── Leg instructions (pickup before in-transit, delivery while in-transit) ──
    // Read straight off viewingLeg (the raw API leg) rather than activeLegData:
    // processOrderData rebuilds the leg and does not carry the instruction columns through.
    const instructionNoteType = useMemo(() => getLegInstructionNoteType(activeLegData?.orderStatus), [activeLegData?.orderStatus]);
    const existingInstructions = useMemo(() => {
        if (!instructionNoteType) return '';

        const value = instructionNoteType === 'pickup' ? viewingLeg?.pickupInstruction : viewingLeg?.deliveryInstruction;

        return value || '';
    }, [instructionNoteType, viewingLeg?.pickupInstruction, viewingLeg?.deliveryInstruction]);

    // Every self-serve action is withdrawn once the freight is delivered. While loading there is
    // no status yet, so the bar stays up and renders its skeletons.
    const isPostDelivery = isPostDeliveryOrderStatus(activeLegData?.orderStatus);

    // A cancelled order is closed: nothing will be picked up, delivered or cleared through customs,
    // so every footer action is a request WAL cannot act on. Checked on BOTH the viewed leg and the
    // order — getLiveLegsSorted drops cancelled legs, so a fully cancelled order falls back to
    // currentLegData and only the leg carries the status. 'cancelled' is deliberately not in
    // POST_DELIVERY_ORDER_STATUSES (those are states an order passes THROUGH after delivery), which
    // is why isPostDelivery doesn't already cover this.
    const isCancelled = normalizeLegStatus(activeLegData?.orderStatus) === 'cancelled' || normalizeLegStatus(order?.orderStatus) === 'cancelled';

    // Carrier-block subtitle id: a single presented leg (direct order, or fully consolidated
    // by Show-as-Multi-Leg OFF) is known to the customer by its MASTER id — the leg's own id
    // (#…-2472 on order #…-2471) reads as a different order. Multi-leg keeps the per-leg id.
    const displayLegId = liveLegs.length > 1 ? activeLegData?.legId : order?.orderId || activeLegData?.legId;

    // Deep link from the dashboard's "Add Your Customs Documents" action item: open the upload
    // modal on arrival instead of leaving the customer to find the button on the page. Held until
    // the order has loaded because the modal files against `customsLegId` — the leg that actually
    // crosses the border — which is only resolvable from the loaded legs.
    const customsDeepLinkHandledRef = useRef(false);

    useEffect(() => {
        if (!location.state?.openCustomsUpload || customsDeepLinkHandledRef.current) return;
        if (isLoading || !order) return;

        customsDeepLinkHandledRef.current = true;
        // Consume the flag so a refresh or a back-navigation to this order doesn't reopen the
        // modal — react-router keeps location state in history.state across reloads.
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });

        // Same branches the Upload Customs Docs button takes, so the deep link can never reach a
        // state the button itself wouldn't: a read-only view asks the viewer to log in, and an
        // order that is not cross-border (or already delivered) simply has nothing to upload.
        if (isReadOnly) {
            handleActionClick(true);

            return;
        }

        // Nothing to upload when the leg already has its customs document — the dashboard review
        // item can outlive the upload, and opening an empty modal over it would read as a bug.
        if (isCrossBorder && !isPostDelivery && !hasCustomsDocument) setIsShowUploadCustomsDocModal(true);
    }, [location.state?.openCustomsUpload, isLoading, order, isReadOnly, isCrossBorder, isPostDelivery, hasCustomsDocument]);

    return (
        <>
            <div className="table-box-wrap rounded-xl border border-utilityGray200 shadow-xs  flex-none">
                {/* Carrier Header - 4.1 — matches reference CarrierInfo structure exactly */}
                <div className="border-b rounded-t-xl border-utilityGray200 bg-gray50 p-3 flex items-center gap-x-2.5 relative">
                    {isLoading ? (
                        <>
                            <div className="w-10 h-10 rounded-md flex-none custom-loading" />
                            <div className="flex-1 space-y-0.5">
                                <p className="text-grayLight900 text-sm font-medium custom-loading w-32">Carrier Name</p>
                                <p className="text-gray500 text-xs custom-loading w-20">MC #000000</p>
                            </div>
                        </>
                    ) : isNoCarrierAssigned || isUnAssignedCarrier ? (
                        <>
                            {/* Org identity (appearance table) — uploaded brand logo, black WAL icon as fallback.
                                Also used when a carrier id exists but its record is hidden or missing: the customer
                                is never told a carrier is "unassigned" (customer wording, 2026-09-05). */}
                            {orgBranding.logoSrc ? (
                                <img
                                    src={orgBranding.logoSrc}
                                    alt={orgBranding.name}
                                    className="w-10 h-10 rounded-md border border-utilityBlack border-opacity-[0.08] bg-white object-contain flex-none"
                                />
                            ) : (
                                <div className="w-10 h-10 rounded-md bg-utilityBlack flex items-center justify-center p-1.5 flex-none">
                                    <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                                </div>
                            )}
                            <div className="flex-1 space-y-0.5">
                                {/* Same branding source as the Carrier & Pricing Details card (CarrierAssignment.tsx) — both read useOrgBranding so they can never drift. */}
                                <h6 className="text-grayLight900 text-sm font-medium">{orgBranding.name}</h6>
                                {/* Org MC number (Organization Settings) preferred; leg id only when MC isn't configured */}
                                {orgMcNumber ? (
                                    <p className="text-gray500 text-xs leading-[18px] font-normal">MC #{orgMcNumber}</p>
                                ) : (
                                    displayLegId && <p className="text-gray500 text-xs leading-[18px] font-normal">#{displayLegId}</p>
                                )}
                            </div>
                        </>
                    ) : isUnAssignedCarrier ? (
                        <>
                            <div className="rounded-md bg-utilityGray100 p-1.5">
                                <Truck01 className="w-7 h-7 text-gray500" />
                            </div>
                            <div className="flex-1 space-y-0.5">
                                <h6 className="text-grayLight900 text-sm font-medium">{t('Common.Unassigned_Carrier')}</h6>
                                {displayLegId && <p className="text-gray500 text-xs leading-[18px] font-normal">#{displayLegId}</p>}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Carrier logo / initials avatar — same logic as reference CarrierInfo */}
                            <div className="flex-none relative">
                                {carrierImageSrc && !carrierImageError ? (
                                    <img
                                        src={carrierImageSrc}
                                        className="w-10 h-10 rounded-md border border-utilityBlack border-opacity-[0.08]"
                                        onError={() => setCarrierImageError(true)}
                                        alt={carrierName || ''}
                                    />
                                ) : carrierName ? (
                                    <div className="table-profile-customer-circle-xl !w-10 !h-10 !rounded-md">{getShortName(carrierName)}</div>
                                ) : (
                                    <div className="table-profile-customer-circle-xl !w-10 !h-10 !rounded-md">N/A</div>
                                )}
                            </div>

                            {/* Carrier name + MC # + PRO # — same structure as reference CarrierHeader */}
                            <div className="flex-1 min-w-0">
                                <h6 className="text-grayLight900 text-sm font-medium truncate">{carrierName || ''}</h6>
                                <div className="flex items-center gap-x-1 flex-wrap">
                                    {mcId && (
                                        <>
                                            <p className="text-grayLight600 text-xs leading-[18px] font-normal">MC #{mcId}</p>
                                            {carrierOrderId && <span className="border-l border-borderPrimary self-stretch my-[3px]"></span>}
                                        </>
                                    )}
                                    {carrierOrderId && <p className="text-grayLight600 text-xs leading-[18px] font-normal">PRO #{carrierOrderId}</p>}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Leg pager — WAL-style arrows to page the card through legs, independent of the tabs below */}
                    {!isLoading && liveLegs.length > 1 && (
                        <div className="flex items-center gap-x-1 flex-none ml-auto">
                            <ButtonCmp
                                className="btn_secondary_black"
                                size="square-sm"
                                disabled={viewingLegIndex <= 0}
                                onClick={() => setViewingLegIndex((prev) => Math.max(0, prev - 1))}
                                icon={<ChevronLeft className="w-4 h-4" />}
                            >
                                <></>
                            </ButtonCmp>
                            <ButtonCmp
                                className="btn_secondary_black"
                                size="square-sm"
                                disabled={viewingLegIndex >= liveLegs.length - 1}
                                onClick={() => setViewingLegIndex((prev) => Math.min(liveLegs.length - 1, prev + 1))}
                                icon={<ChevronRight className="w-4 h-4" />}
                            >
                                <></>
                            </ButtonCmp>
                        </div>
                    )}

                    {/* Contact button — shown when salesRep data is available */}
                    {!isReadOnly && salesRep && (salesRep.phone || salesRep.salesrepemail) && (
                        <div ref={contactPopoverRef} className={`relative flex-none ${!isLoading && liveLegs.length > 1 ? '' : 'ml-auto'}`}>
                            <ButtonCmp className="btn_secondary_black" onClick={() => setIsContactPopoverOpen((prev) => !prev)} icon={<PhoneCall01 className="w-4 h-4" />}>
                                <span className="text-xs font-medium">{t('Common.Contact')}</span>
                            </ButtonCmp>

                            {isContactPopoverOpen && (
                                <div className="absolute right-0 top-full mt-1 z-10 bg-white border border-utilityGray200 rounded-[10px] p-2 max-w-[280px] min-w-[240px] space-y-2.5 shadow-[0_0_16px_-4px_rgba(16,24,40,0.12)]">
                                    {salesRep.phone && (
                                        <div className="flex items-start justify-between gap-x-2 border border-utilityGray20 p-3 rounded-md">
                                            <div className="min-w-0 flex flex-col">
                                                <span className="text-textSecondary text-[12px] font-semibold leading-[18px]">{t('Common.Contact_Number')}</span>
                                                <PhoneNumberDisplay phoneNumber={salesRep.phone} className="text-gray500 !text-[12px] leading-[18px] !font-normal" showLink={false} />
                                            </div>
                                            <TooltipCmp message="Copy" childrenClassName="flex">
                                                <span className="flex-none text-textSecondary" onClick={() => handleCopy(salesRep.phone)}>
                                                    <Copy06 className="w-4 h-4 cursor-pointer" />
                                                </span>
                                            </TooltipCmp>
                                        </div>
                                    )}
                                    {salesRep.salesrepemail && (
                                        <div className="flex items-start justify-between gap-x-2 border border-utilityGray20 p-3 rounded-md">
                                            <div className="min-w-0 flex flex-col">
                                                <span className="text-textSecondary text-[12px] font-semibold leading-[18px]">{t('Common.Contact_Email')}</span>
                                                <TruncateTextCmp message={salesRep.salesrepemail} refName={salesRepEmailRef}>
                                                    <p ref={salesRepEmailRef} className="text-gray500 !text-[12px] leading-[18px] !font-normal truncate">
                                                        {salesRep.salesrepemail}
                                                    </p>
                                                </TruncateTextCmp>
                                            </div>
                                            <TooltipCmp message="Copy" childrenClassName="flex">
                                                <span className="flex-none text-textSecondary" onClick={() => handleCopy(salesRep.salesrepemail)}>
                                                    <Copy06 className="w-4 h-4 cursor-pointer" />
                                                </span>
                                            </TooltipCmp>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Status Text - 4.2 */}
                <div className="px-3 py-3 border-b border-utilityGray200 flex items-center justify-between gap-x-3 bg-white">
                    {isLoading ? (
                        <div className="space-y-0.5 flex-1 min-w-0">
                            <h6 className="text-grayLight900 text-[15px] leading-[18px] font-semibold custom-loading w-fit">Lorem ipsum dolor sit ametetd</h6>
                            <p className="text-grayLight600 text-xs leading-[18px] font-normal custom-loading w-fit">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Lorem</p>
                        </div>
                    ) : (
                        <TitleDescriptionSection title={effectiveTitleParts.title} description={effectiveTitleParts.description} functions={effectiveTitleParts.functions} />
                    )}
                </div>

                {/* Progress Stepper (per viewed leg) */}
                <div className="bg-white rounded-b-xl">
                    <ProgressList
                        isProgressLoading={isLoading}
                        viewingLeg={viewingLeg}
                        orderDetails={order}
                        isFirstLeg={isFirstLeg}
                        isFinalLeg={isFinalLeg}
                        isLegReached={isLegReached}
                        dropsHere={!isFinalLeg && !!nbaContext.itemsDelivered}
                    />
                </div>
                {/* Footer: leg indicator (multi-leg) + action buttons */}
                {((!isPostDelivery && !isCancelled) || liveLegs.length > 1) && (
                    <div className="px-3 py-3 flex items-center justify-between gap-x-3 border-t border-utilityGray200 bg-white rounded-b-xl">
                        {liveLegs.length > 1 && viewingLeg && (
                            <p className="text-grayLight600 text-xs italic font-medium flex-none">
                                {getCardFooterLabel({ legsDisplayed, numbering: legNumbering, viewingIndex: viewingLegIndex, legCount: liveLegs.length, legId: viewingLeg?.legId })}
                            </p>
                        )}
                        {/* Labels never wrap: buttons share the row and grow; one that no longer fits drops to its own full-width row (mobile). */}
                        <div className="flex flex-wrap gap-2 lg:justify-end flex-1">
                            {!isPostDelivery && !isCancelled && !hasCustomsDocument && (isLoading || isCrossBorder) && (
                                <ButtonCmp
                                    className={`btn-outline-primary !gap-0 whitespace-nowrap max-lg:flex-[1_1_auto] ${isLoading ? 'custom-loading' : ''}`}
                                    onClick={() => (isReadOnly ? handleActionClick(true) : setIsShowUploadCustomsDocModal(true))}
                                    disabled={isLoading}
                                >
                                    {t('Orders.Upload_Customs_Docs')}
                                    <span className="text-red-600 leading-4 text-xs">*</span>
                                </ButtonCmp>
                            )}
                            {!isCancelled && (isLoading || activeLegData?.orderStatus === 'in-transit') && (
                                <ButtonCmp
                                    className={`btn-outline-primary !gap-0 whitespace-nowrap max-lg:flex-[1_1_auto] ${isLoading || isRequestingUpdateLocation ? 'custom-loading' : ''}`}
                                    disabled={!isReadOnly && (isLoading || isUpdateLocationPending || isRequestingUpdateLocation)}
                                    onClick={() => (isReadOnly ? handleActionClick(true) : handleRequestUpdateLocation())}
                                >
                                    {isUpdateLocationPending ? t('Orders.Location_Update_Requested') : t('Orders.Request_Update_Location')}
                                </ButtonCmp>
                            )}
                            {!isCancelled && (isLoading || instructionNoteType) && (
                                <ButtonCmp
                                    className={`btn-outline-primary whitespace-nowrap max-lg:flex-[1_1_auto] ${isLoading ? 'custom-loading !border-0' : ''}`}
                                    onClick={() => (isReadOnly ? handleActionClick(true) : setIsShowLegInstructionsModal(true))}
                                    disabled={isLoading}
                                >
                                    {instructionNoteType === 'delivery'
                                        ? t(existingInstructions ? 'Orders.Update_Delivery_Instructions' : 'Orders.Add_Delivery_Instructions')
                                        : t(existingInstructions ? 'Orders.Update_Pickup_Instructions' : 'Orders.Add_Pickup_Instructions')}
                                </ButtonCmp>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {isShowLoginRequiredModal && (
                <CommonModal
                    title={<h6 className="text-textSecondary text-base lg:text-lg font-semibold">{t('Orders.Login_Required_Title')}</h6>}
                    modalTitleDesc={<p className="text-grayLight600 text-xs leading-[18px] lg:text-sm font-normal">{t('Orders.Login_Required_Desc')}</p>}
                    modalHeaderClass="border-b-0 !px-6 !pt-6 !pb-5 !bg-transparent"
                    modalHeaderTextClass="!flex-col"
                    modalFooterClass="border-t-0 !bg-transparent"
                    size="max-w-[445px]"
                    primaryBtnText={t('QuotingHub.Common.Continue') as string}
                    secondaryBtnText={t('Common.Cancel') as string}
                    primaryBtnOnClick={() => navigate(ROUTES.LOGIN)}
                    secondaryBtnOnClick={() => handleActionClick(false)}
                    handleClose={() => handleActionClick(false)}
                >
                    <></>
                </CommonModal>
            )}
            {isShowUploadCustomsDocModal && (
                <UploadCustomsDocModal
                    handleClose={() => setIsShowUploadCustomsDocModal(false)}
                    // Always the crossing leg, never the presented span's identity leg (see customsLegId).
                    orderLegId={customsLegId}
                    handleOnSubmit={handleCustomsDocUploadSuccess}
                />
            )}
            {isShowLegInstructionsModal && instructionNoteType && (
                <LegInstructionsModal
                    handleClose={() => setIsShowLegInstructionsModal(false)}
                    // A consolidated leg spans several physical legs (mergedLegIds). Delivery
                    // instructions belong to the leg that actually delivers — the LAST physical
                    // leg of the span (getDeliveringLegId); pickup instructions to the first (the leg's own id).
                    orderLegId={instructionNoteType === 'delivery' ? getDeliveringLegId(viewingLeg) : activeLegData?.orderLegId || activeLegData?.id}
                    noteType={instructionNoteType}
                    initialInstructions={existingInstructions}
                    onSuccess={(noteType, instructions, activity) => {
                        // Saved directly on the leg (no approval step), so reflect it in Redux
                        // right away — the button flips to "Update …" without a refetch.
                        const field = noteType === 'pickup' ? 'pickupInstruction' : 'deliveryInstruction';
                        patchViewingLegField(field, instructions);
                        setActiveLegData((prev: any) => ({ ...prev, [field]: instructions }));
                        if (activity) dispatch(prependActivity(activity));
                    }}
                />
            )}
        </>
    );
};

export default OrderStatus;
